import { legGroupLabel, type ParlayLabelColor } from '@parlay-picker/shared/scoreDisplay';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';

/** Text colour class for a score's display colour. */
export function getScoreTextClass(displayColor: ParlayLabelColor): string {
  switch (displayColor) {
    case 'success':
      return 'text-theme-success';
    case 'lean':
      return 'text-teal-400';
    case 'danger':
      return 'text-theme-danger';
    case 'none':
      return 'text-slate-400';
    case 'caution':
      return 'text-theme-warning';
  }
}

/**
 * Get badge styling based on the normalized display score color.
 * Uses displayColor derived from the 0-100 normalized score.
 */
export function getBadgeStyle(displayColor: ParlayLabelColor, hasWarnings: boolean) {
  if (hasWarnings) {
    return {
      bg: 'bg-gradient-to-r from-orange-500 to-amber-400',
      border: 'border-theme-warning',
      text: 'text-white',
      icon: '⚠️',
      label: 'Caution',
    };
  }

  if (displayColor === 'success') {
    return {
      bg: 'bg-gradient-to-r from-theme-success to-emerald-400',
      border: 'border-theme-success',
      text: 'text-white',
      icon: '🎯',
      label: 'Strong',
    };
  }

  if (displayColor === 'lean') {
    return {
      bg: 'bg-gradient-to-r from-teal-500 to-emerald-400',
      border: 'border-teal-400',
      text: 'text-white',
      icon: '📈',
      label: 'Lean',
    };
  }

  if (displayColor === 'none') {
    return {
      bg: 'bg-gradient-to-r from-slate-500 to-slate-400',
      border: 'border-slate-400',
      text: 'text-white',
      icon: '⏸️',
      label: 'No signal',
    };
  }

  if (displayColor === 'danger') {
    return {
      bg: 'bg-gradient-to-r from-theme-danger to-rose-400',
      border: 'border-theme-danger',
      text: 'text-white',
      icon: '⛔',
      label: 'Skip',
    };
  }

  // caution
  return {
    bg: 'bg-gradient-to-r from-theme-warning to-amber-400',
    border: 'border-theme-warning',
    text: 'text-white',
    icon: '📊',
    label: 'Caution',
  };
}

/**
 * Format leg label for display (truncate if too long)
 */
export function formatLegLabel(playerName?: string, propType?: string): string {
  if (!playerName) return '';
  const name = playerName.length > 15 ? `${playerName.slice(0, 12)}...` : playerName;
  return legGroupLabel(name, propType);
}

/**
 * Strip the player name prefix from a factor string when displayed inside a leg group.
 * e.g., "LeBron James pts performance" → "pts performance"
 */
export function stripPlayerPrefix(factorText: string, playerName?: string): string {
  if (!playerName) return factorText;
  if (factorText.startsWith(`${playerName} `)) {
    return factorText.slice(playerName.length + 1);
  }
  return factorText;
}

/** Resolve a prop highlight key to a formatted label + direction from leg data */
export function formatPropBadge(
  prop: string,
  legs?: Array<{ statType?: string; propType?: string; overUnder?: string | null }>,
): { label: string; direction: 'Over' | 'Under' | null } {
  const label = formatStatLabel(prop);
  const leg = legs?.find((l) => (l.statType || l.propType || '').toLowerCase() === prop);
  return { label, direction: (leg?.overUnder as 'Over' | 'Under') ?? null };
}
